/**
 * YouTube paints a center play/pause bezel on a click, then fades it in about
 * a second. The control-bar play button does not fade while the video stays
 * paused: pause cancels autohide, so that button remains until playback resumes.
 * Waiting on the watched player therefore cannot produce a clean still.
 *
 * A second player with controls=0, paused through the IFrame API, never takes
 * that click path, so neither button is drawn. The shot is taken after that
 * player has actually painted the requested time.
 */

/** Extra time after the API pause so the video frame is on screen. */
export const CLEAN_FRAME_PAINT_MS = 300;

export const CLEAN_FRAME_TIMEOUT_MS = 8000;

/**
 * Accept a pause only after the clean player has played and landed on the moment.
 * The initial paused/cued state is the poster, not the frame.
 * @param {{ seenPlaying?: boolean, currentTime?: number, targetSeconds?: number }} state
 */
export function shouldAcceptCleanPause({ seenPlaying, currentTime, targetSeconds } = {}) {
  if (!seenPlaying) return false;
  const now = Number(currentTime);
  const target = Number(targetSeconds);
  if (!Number.isFinite(now) || !Number.isFinite(target)) return false;
  return Math.abs(now - target) <= 1.5;
}

let youtubeApiPromise = null;

export function loadYouTubeIframeApi() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (youtubeApiPromise) return youtubeApiPromise;
  youtubeApiPromise = new Promise((resolve, reject) => {
    let settled = false;
    const succeed = () => {
      if (settled) return;
      if (!window.YT?.Player) return;
      settled = true;
      resolve(window.YT);
    };
    const fail = (error) => {
      if (settled) return;
      settled = true;
      youtubeApiPromise = null;
      reject(error instanceof Error ? error : new Error('YouTube player unavailable'));
    };
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previous === 'function') previous();
      succeed();
    };
    const poll = setInterval(() => {
      if (window.YT?.Player) {
        clearInterval(poll);
        succeed();
      }
    }, 100);
    setTimeout(() => {
      clearInterval(poll);
      fail(new Error('YouTube player unavailable'));
    }, 5000);
    if (window.YT?.Player) {
      clearInterval(poll);
      succeed();
      return;
    }
    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      script.onerror = () => fail(new Error('YouTube player failed to load'));
      document.head.appendChild(script);
    }
  });
  return youtubeApiPromise;
}

function delay(ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

/**
 * Cover `mount` with a chromeless player paused at `seconds`.
 * Resolves once that frame is painted. Call `destroy` after the screenshot.
 * @param {HTMLElement} mount
 * @param {string} videoId
 * @param {number} seconds
 */
export function openCleanFrame(mount, videoId, seconds) {
  let player = null;
  let destroyed = false;
  const target = Number(seconds);
  const ready = loadYouTubeIframeApi().then(YT => new Promise((resolve, reject) => {
    if (destroyed) {
      reject(new Error('clean frame closed'));
      return;
    }
    const slot = document.createElement('div');
    slot.className = 'board-youtube-clean-capture-slot';
    mount.appendChild(slot);
    const timeout = setTimeout(() => reject(new Error('clean frame timed out')), CLEAN_FRAME_TIMEOUT_MS);
    let seenPlaying = false;
    const finish = () => {
      clearTimeout(timeout);
      resolve();
    };
    const fail = error => {
      clearTimeout(timeout);
      reject(error instanceof Error ? error : new Error('clean frame failed'));
    };
    player = new YT.Player(slot, {
      host: 'https://www.youtube-nocookie.com',
      videoId,
      playerVars: {
        autoplay: 1,
        mute: 1,
        controls: 0,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
        disablekb: 1,
        fs: 0,
        iv_load_policy: 3,
        cc_load_policy: 0,
        start: Math.max(0, Math.floor(target)),
        origin: window.location.origin,
      },
      events: {
        onReady: event => {
          try {
            event.target.mute();
            event.target.seekTo(target, true);
            event.target.playVideo();
          } catch (err) {
            fail(err);
          }
        },
        onStateChange: event => {
          if (event.data === YT.PlayerState.PLAYING) {
            seenPlaying = true;
            try {
              event.target.pauseVideo();
            } catch (err) {
              fail(err);
            }
            return;
          }
          if (event.data !== YT.PlayerState.PAUSED) return;
          let currentTime = 0;
          try {
            currentTime = event.target.getCurrentTime();
          } catch {
            currentTime = 0;
          }
          if (!shouldAcceptCleanPause({ seenPlaying, currentTime, targetSeconds: target })) return;
          delay(CLEAN_FRAME_PAINT_MS).then(finish);
        },
        onError: () => fail(new Error('clean frame player error')),
      },
    });
  }));

  return {
    ready,
    destroy() {
      destroyed = true;
      try {
        player?.destroy?.();
      } catch {
        /* already gone */
      }
      player = null;
    },
  };
}
